import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Checkbox, FormControlLabel, Popover, Chip, Radio, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AppsIcon from '@mui/icons-material/Apps';
import GradeIcon from '@mui/icons-material/Grade';
import LanguageIcon from '@mui/icons-material/Language';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import CategoryIcon from '@mui/icons-material/Category';
import SchoolIcon from '@mui/icons-material/School';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import WorkIcon from '@mui/icons-material/Work';
import './StaffFilterPanel.css';
import { MainContext } from '../../../../../../context/main/main-context';
import SimpleReactValidator from "simple-react-validator";

// Filter icons mapping for staff users
const filterIconMap = {
    Subject: <MenuBookIcon fontSize="small" />,
    Grade: <GradeIcon fontSize="small" />,
    Language: <LanguageIcon fontSize="small" />,
    Exam_Category: <AppsIcon fontSize="small" />,
    Category: <CategoryIcon fontSize="small" />,
    Status: <AssignmentIcon fontSize="small" />,
    Year: <CalendarTodayIcon fontSize="small" />,
    Created_By: <PersonIcon fontSize="small" />,
    Exam_Status: <AssignmentIcon fontSize="small" />,
    Department: <WorkIcon fontSize="small" />,
};

// Enhanced chip color mapping with staff-specific styling
const chipColorMap = {
    Exam_Category: { bg: '#FFF3E0', color: '#E65100', border: '#FFCC80' },
    Grade: { bg: '#FCE4EC', color: '#C2185B', border: '#F48FB1' },
    Language: { bg: '#E3F2FD', color: '#1565C0', border: '#90CAF9' },
    Subject: { bg: '#F3E5F5', color: '#7B1FA2', border: '#CE93D8' },
    Category: { bg: '#E8F5E9', color: '#2E7D32', border: '#A5D6A7' },
    Status: { bg: '#FFF8E1', color: '#F57F17', border: '#FFE082' },
    Year: { bg: '#E0F7FA', color: '#00838F', border: '#80DEEA' },
    Created_By: { bg: '#EDE7F6', color: '#512DA8', border: '#B39DDB' },
    Exam_Status: { bg: '#FFEBEE', color: '#C62828', border: '#EF9A9A' },
    Department: { bg: '#E8EAF6', color: '#3F51B5', border: '#9FA8DA' },
    default: { bg: '#F5F5F5', color: '#616161', border: '#E0E0E0' },
};

const filterKeyMap = {
    Subject: { idKey: "subject_id", labelKey: "subject" },
    Grade: { idKey: "grade_id", labelKey: "grade" },
    Status: { idKey: "status_id", labelKey: "status" },
    Source: { idKey: "source_id", labelKey: "source" },
    Author: { idKey: "author_id", labelKey: "author" },
    Category: { idKey: "category_id", labelKey: "name" },
    Exam_Category: { idKey: "exam_category_id", labelKey: "name" },
    Chapter: { idKey: "chapter_id", labelKey: "chapter" },
    Difficulty: { idKey: "difficulty_id", labelKey: "difficulty_type" },
    Language: { idKey: "language_id", labelKey: "language" },
    Created_By: { idKey: "created_by_id", labelKey: (item) => `${item.first_name} ${item.last_name}` },
    Reviewers: { idKey: "user_id", labelKey: (item) => `${item.first_name} ${item.last_name}` },
    Term: { idKey: "term_id", labelKey: "term" },
    Exam_Status: { idKey: "exam_status_id", labelKey: "exam_status" },
    Year: { idKey: "year_id", labelKey: "year" },
    Type: { idKey: "type_id", labelKey: "qtype" },
    Department: { idKey: "department_id", labelKey: "department_name" }
};

export default function StaffFilterPanel({
    userType, selectedFilters, setSelectedFilters,
    filterOptions, setFilterOptions, visibleFilterOptions, isShowExamStatus
}) {

    const [anchorEl, setAnchorEl] = useState(null);
    const [currentDropdown, setCurrentDropdown] = useState('');
    const [showAllChips, setShowAllChips] = useState({});
    const [, forceUpdate] = useState();

    const handleOpen = (event, filterType) => {
        setAnchorEl(event.currentTarget);
        setCurrentDropdown(filterType);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setCurrentDropdown('');
    };

    const handleToggle = (filterType, value) => {
        if (filterType === "Exam_Status") {
            setSelectedFilters({
                ...selectedFilters,
                [filterType]: [value]
            });
        } else {
            const current = selectedFilters[filterType] || [];
            const updated = current.includes(value)
                ? current.filter((item) => item !== value)
                : [...current, value];

            setSelectedFilters({
                ...selectedFilters,
                [filterType]: updated
            });
        }
    };

    const handleDeleteChip = (filterType, value) => {
        const updatedFilters = {
            ...selectedFilters,
            [filterType]: selectedFilters[filterType].filter((item) => item !== value)
        };

        setSelectedFilters(updatedFilters);
    };

    const handleClearAll = () => {
        const cleared = {};
        Object.keys(selectedFilters).forEach((key) => {
            if (isShowExamStatus && key === "Exam_Status") {
                cleared[key] = selectedFilters[key];
            } else {
                cleared[key] = [];
            }
        });
        setSelectedFilters(cleared);
    };

    const toggleShowAllChips = (filterType) => {
        setShowAllChips(prev => ({
            ...prev,
            [filterType]: !prev[filterType]
        }));
    };

    const open = Boolean(anchorEl);

    const [validator] = useState(
        new SimpleReactValidator({
            messages: {
                className: 'srv-validation-message',
                required: "This field is required.",
                before: "Start date must be before end date.",
                after: "End date must be after start date.",
            },
            validators: {
                before: {
                    message: 'Start date must be before end date.',
                    rule: (val, params, validator) => {
                        if (!val || !params[0]) return true;
                        const startDate = new Date(val);
                        const endDate = new Date(params[0]);
                        return startDate <= endDate;
                    }
                },
                after: {
                    message: 'End date must be after start date.',
                    rule: (val, params, validator) => {
                        if (!val || !params[0]) return true;
                        const startDate = new Date(params[0]);
                        const endDate = new Date(val);
                        return endDate >= startDate;
                    }
                }
            }
        })
    );

    const handleStartDateChange = (event) => {
        const date = event.target.value;
        const formattedDate = date ? date.replace(/-/g, "") : null;
        const newFilters = {
            ...selectedFilters,
            StartDate: formattedDate,
            StartDateRaw: date
        };
        setSelectedFilters(newFilters);
        validator.showMessageFor("StartDate");
    };

    const handleEndDateChange = (event) => {
        const date = event.target.value;
        const formattedDate = date ? date.replace(/-/g, "") : null;
        const newFilters = {
            ...selectedFilters,
            EndDate: formattedDate,
            EndDateRaw: date
        };
        setSelectedFilters(newFilters);
        validator.showMessageFor("EndDate");
    };

    // Render filter chips
    const renderFilterChips = () => (
        <Stack direction="row" spacing={0.5} className="staff-selected-chips-row" sx={{ flexWrap: 'wrap' }}>
            {visibleFilterOptions
                ?.filter(filterType => filterType !== 'StartDate' && filterType !== 'EndDate' && (isShowExamStatus || filterType !== "Exam_Status"))
                .flatMap((filterType) => {
                    const chipColors = chipColorMap[filterType] || chipColorMap.default;
                    const config = filterKeyMap[filterType];
                    return (selectedFilters[filterType] || []).map((value) => {
                        const item = (filterOptions[filterType] || []).find(opt => opt[config?.idKey] === value);
                        const label = typeof config?.labelKey === 'function' ? config.labelKey(item) : item?.[config?.labelKey] || value;

                        return (
                            <Chip
                                key={`${filterType}-${value}`}
                                label={label}
                                onDelete={filterType !== "Exam_Status" ? () => handleDeleteChip(filterType, value) : undefined}
                                size="small"
                                className="staff-selected-chip"
                                sx={{
                                    backgroundColor: chipColors.bg,
                                    color: chipColors.color,
                                    border: `1px solid ${chipColors.border}`,
                                    fontWeight: 600,
                                    height: 'auto',
                                    fontSize: 10,
                                    padding: '1px 4px',
                                    boxShadow: 'none',
                                }}
                            />
                        );
                    });
                })}
        </Stack>
    );

    return (
        <div className="staff-filter-panel-fullwidth">
            {/* Single row container with all filter elements */}
            <div className="staff-filters-unified-container">
                {/* Date Range Filters (moved to top) */}
                {(visibleFilterOptions.includes("StartDate") || visibleFilterOptions.includes("EndDate")) && (
                    <div className="staff-date-filters-row">
                        <label className="staff-date-label">
                            <span>Start Date:</span>
                            <input
                                type="date"
                                className="staff-date-input"
                                value={selectedFilters.StartDateRaw || ""}
                                onChange={handleStartDateChange}
                            />
                            {validator.message("StartDate", selectedFilters.StartDateRaw, `before:${selectedFilters.EndDateRaw}`)}
                        </label>

                        <label className="staff-date-label">
                            <span>End Date:</span>
                            <input
                                type="date"
                                className="staff-date-input"
                                value={selectedFilters.EndDateRaw || ""}
                                onChange={handleEndDateChange}
                            />
                            {validator.message("EndDate", selectedFilters.EndDateRaw, `after:${selectedFilters.StartDateRaw}`)}
                        </label>
                    </div>
                )}

                {/* Filter Dropdowns (clear button inline) */}
                <div className="staff-filter-dropdowns-row">
                    {userType && visibleFilterOptions
                        ?.filter(filterType => filterType !== 'StartDate' && filterType !== 'EndDate' && (isShowExamStatus || filterType !== "Exam_Status"))
                        .map((filterType) => {
                            const isActive = selectedFilters[filterType]?.length > 0;
                            const displayName = filterType.replace(/_/g, ' ');

                            return (
                                <button
                                    key={filterType}
                                    className={`staff-filter-dropdown-btn ${isActive ? 'active' : ''} ${currentDropdown === filterType ? 'open' : ''}`}
                                    onClick={(e) => handleOpen(e, filterType)}
                                >
                                    <span className="staff-filter-icon">{filterIconMap[filterType] || <AppsIcon fontSize="small" />}</span>
                                    <span className="staff-filter-label">{displayName}</span>
                                    <KeyboardArrowDownIcon className="staff-filter-arrow" />
                                </button>
                            );
                        })}

                    {/* Inline Clear Button next to filters */}
                    <button className="staff-clear-filters-btn staff-clear-inline" onClick={handleClearAll} aria-label="Clear Filters">
                        <DeleteForeverIcon fontSize="small" />
                        <span className="ms-1 d-none d-md-inline">Clear Filters</span>
                    </button>
                </div>

            </div>

            {/* Popover for filter options */}
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ className: 'staff-dropdown-popover' }}
            >
                <div className="staff-dropdown-header d-flex justify-content-between align-items-center">
                    {currentDropdown !== "Exam_Status" && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={
                                        filterOptions[currentDropdown]?.length > 0 &&
                                        filterOptions[currentDropdown]?.every(opt => {
                                            const config = filterKeyMap[currentDropdown];
                                            const value = opt[config.idKey];
                                            return selectedFilters[currentDropdown]?.includes(value);
                                        })
                                    }
                                    indeterminate={
                                        selectedFilters[currentDropdown]?.length > 0 &&
                                        selectedFilters[currentDropdown]?.length < filterOptions[currentDropdown]?.length
                                    }
                                    onChange={(e) => {
                                        const config = filterKeyMap[currentDropdown];
                                        if (e.target.checked) {
                                            const allValues = filterOptions[currentDropdown]?.map(opt => opt[config.idKey]) || [];
                                            setSelectedFilters({
                                                ...selectedFilters,
                                                [currentDropdown]: allValues
                                            });
                                        } else {
                                            setSelectedFilters({
                                                ...selectedFilters,
                                                [currentDropdown]: []
                                            });
                                        }
                                    }}
                                    size="small"
                                    sx={{
                                        color: '#7C3AED',
                                        '&.Mui-checked': { color: '#7C3AED' },
                                    }}

                                />
                            }
                            label="Select All"
                        />
                    )}
                    <CloseIcon fontSize="small" onClick={handleClose} style={{ cursor: 'pointer' }} />
                </div>

                <div className="staff-dropdown-body">
                    {(filterOptions[currentDropdown] || []).map((option, idx) => {
                        const value =
                            option?.created_by_id ||
                            option?.user_id ||
                            option?.grade_id ||
                            option?.language_id ||
                            option?.subject_id ||
                            option?.term_id ||
                            option?.year_id ||
                            option?.source_id ||
                            option?.status_id ||
                            option?.author_id ||
                            option?.chapter_id ||
                            option?.category_id ||
                            option?.exam_category_id ||
                            option?.difficulty_id ||
                            option?.type_id ||
                            option?.exam_status_id ||
                            option?.department_id;

                        const label =
                            option?.name ||
                            option?.grade ||
                            option?.language ||
                            option?.subject ||
                            option?.term ||
                            option?.year ||
                            option?.source ||
                            option?.status ||
                            option?.author ||
                            option?.chapter ||
                            option?.difficulty_type ||
                            option?.exam_status ||
                            option?.qtype ||
                            option?.department_name ||
                            `${option.first_name} ${option.last_name}` ||
                            "Unknown";

                        return (
                            <FormControlLabel
                                key={`${value}-${idx}`}
                                control={
                                    currentDropdown === "Exam_Status" ?
                                        <Radio
                                            checked={selectedFilters[currentDropdown]?.includes(value)}
                                            onChange={() => handleToggle(currentDropdown, value)}
                                            value={value}
                                            sx={{
                                                color: '#7C3AED',
                                                '&.Mui-checked': {
                                                    color: '#7C3AED',
                                                },
                                            }}
                                        />
                                        :
                                        <Checkbox
                                            checked={selectedFilters[currentDropdown]?.includes(value)}
                                            onChange={() => handleToggle(currentDropdown, value)}
                                            size="small"
                                            sx={{
                                                color: '#7C3AED',
                                                '&.Mui-checked': {
                                                    color: '#7C3AED',
                                                },
                                            }}
                                        />
                                }
                                label={label}
                            />
                        );
                    })}
                </div>
            </Popover>
        </div>
    );
}